import { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  type MeetingAttendeeBaseFragment,
  MeetingsMeetingProviderDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { type MeetingType, type PageType } from '../../MeetingsMeetingContext'

interface UseMeetingDataReturn {
  meeting: MeetingType | null
  page: PageType | null
  notes: string | null
  loading: boolean
  error: boolean
  errorMessage: string | null
  resetError: () => void
}

export const useMeetingData = (): UseMeetingDataReturn => {
  const { id: meetingId } = useParams<{ id: string }>()
  const { employee: currentEmployee } = useCurrentSession()

  const [notes, setNotesState] = useState<string | null>(null)
  const [page, setPage] = useState<PageType | null>(null)
  const [meeting, setMeeting] = useState<MeetingType | null>(null)
  const [error, setError] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Single query to get attendee, meeting, and page data
  const { data: attendeeData, loading: loadingAttendee } = useQuery(
    MeetingsMeetingProviderDocument,
    {
      variables: {
        meetingId: Number(meetingId),
        employeeId: Number(currentEmployee?.id),
      },
      skip: !meetingId || !currentEmployee?.id,
    }
  )

  const processAttendeeData = useCallback(() => {
    const attendee = attendeeData?.meetings.attendeesConnection.nodes[0]

    if (!attendee?.meeting) return null

    return {
      id: attendee.meeting.id ?? undefined,
      title: attendee.meeting.title ?? undefined,
      meetingType: attendee.meeting.meetingType ?? undefined,
      attendees: attendee.meeting.attendees.map((meetingAttendee: MeetingAttendeeBaseFragment) => ({
        id: meetingAttendee?.id ?? undefined,
        employee: {
          id: meetingAttendee?.employee?.id ?? undefined,
          avatar: meetingAttendee?.employee?.avatar ?? undefined,
          fullName: meetingAttendee?.employee?.fullName ?? undefined,
          firstName: meetingAttendee?.employee?.firstName ?? undefined,
          lastName: meetingAttendee?.employee?.lastName ?? undefined,
        },
      })),
      organizer: attendee.meeting.organizer
        ? {
            id: attendee.meeting.organizer.id ?? undefined,
            avatar: attendee.meeting.organizer.avatar ?? undefined,
            firstName: attendee.meeting.organizer.firstName ?? undefined,
            fullName: attendee.meeting.organizer.fullName ?? undefined,
            lastName: attendee.meeting.organizer.lastName ?? undefined,
          }
        : undefined,
    }
  }, [attendeeData])

  // Extract page data directly from attendeeData
  const extractPageData = useCallback(() => {
    const pageRaw = attendeeData?.meetings.attendeesConnection.nodes[0]?.page

    if (!pageRaw) {
      return {
        page: null,
        notes: null,
      }
    }

    return {
      page: {
        id: pageRaw.id,
        title: pageRaw.title,
        updatedAt: pageRaw.updatedAt,
      },
      notes: pageRaw.content ?? '',
    }
  }, [attendeeData])

  // Process attendee data when it changes
  useEffect(() => {
    if (attendeeData) {
      const meetingData = processAttendeeData()
      if (meetingData) {
        setMeeting(meetingData)
      }
    }
  }, [attendeeData, processAttendeeData])

  // Process page data when attendee data changes
  useEffect(() => {
    if (attendeeData !== undefined) {
      const { page: pageDataProcessed, notes: notesDataProcessed } = extractPageData()

      setPage(pageDataProcessed)
      setNotesState(notesDataProcessed)
    }
  }, [attendeeData, extractPageData])

  const resetError = useCallback(() => {
    setError(false)
    setErrorMessage(null)
  }, [])

  return {
    meeting,
    page,
    notes,
    loading: loadingAttendee,
    error,
    errorMessage,
    resetError,
  }
}
